import { State } from 'interfaces/state'
import { createSelector } from 'reselect'
import * as isNumber from 'lodash/isNumber'
import * as last from 'lodash/last'
import { Guards } from 'interfaces/guards'
import { connectShadedLoadable, createParallelGuard, connectLoadable } from 'helpers/loadableHelpers'
import { MoneyflowTrendOwnProps } from 'interfaces/moneyflow'
import { SecondaryLine } from 'interfaces/chart'
import { memberMoneyflowTrendGuard, memberOppositeMoneyflowTrendsGuard, moneyflowTrackersGuard, peergroupMoneyflowTrendsGuard } from 'guards/moneyflowGuards'
import { peergroupsGuard } from 'guards/globalGuards'
import {
  memberMoneyflowTrendsSelector,
  memberOppositeTotalMoneyflowTrendSelector,
  moneyflowTrackerLimitSelector,
  othersMoneyflowTrendsSelector,
  yourPeersMoneyflowTrendsSelector,
} from 'selectors/moneyflowSelectors'
import { createTimeperiod } from 'helpers/dateTimeHelpers'
import { buildDatesXMap, buildDividersOneMonth, buildSectionsOneMonth, buildTips, calculateToday, formatHeaderTipDate } from 'helpers/trendHelpers'
import { buildMemberItems, buildMemberProjectedItems, buildPeerPoints, buildTrendDots } from 'helpers/moneyflowHelpers'
import { LineChart, LineChartInnerOwnProps, LineChartInnerStateProps, Tooltip } from 'components/features/pfm/LineChart/LineChart'
import LineChartLoadingOverlay from 'components/atoms/preloaders/LineChartLoadingOverlay'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { selectedRelationSelector } from 'selectors/peergroupSelectors'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

type OwnProps =
  & MoneyflowTrendOwnProps
  & LineChartInnerOwnProps

const createMapStateToProps = () =>
  createSelector(
    memberMoneyflowTrendsSelector,
    yourPeersMoneyflowTrendsSelector,
    othersMoneyflowTrendsSelector,
    moneyflowTrackerLimitSelector,
    memberOppositeTotalMoneyflowTrendSelector,
    selectedRelationSelector,
    isMobileSelector,
    createOwnPropsSelector<OwnProps>(),
    withComparisonSelector,
    (memberTrend, peersTrend, otherTrend, trackerLimit, oppositeTrend, selectedRelation, isMobile, ownProps, withComparison): LineChartInnerStateProps => {
      if (!peersTrend || !otherTrend || !memberTrend) {
        throw createForgottenGuardError('MoneyflowDailyTrendContainer', 'peergroupMoneyflowTrendsGuard')
      }

      if (!selectedRelation) {
        throw createForgottenGuardError('MoneyflowDailyTrendContainer', 'peergroupsGuard')
      }

      const { type, categoryId, timeframe } = ownProps
      const timeperiod = createTimeperiod(timeframe)
      const datesXMap = buildDatesXMap(timeperiod)
      const today = calculateToday(memberTrend.actual, timeframe)

      const memberItems = buildMemberItems(memberTrend.actual, datesXMap)
      const memberProjectedItems = buildMemberProjectedItems(memberTrend, datesXMap)
      const { actual: peersItems, projected: peersProjectedItems } = buildPeerPoints(peersTrend, timeperiod.start_date, today, datesXMap)
      const { actual: otherItems, projected: otherProjectedItems } = buildPeerPoints(otherTrend, timeperiod.start_date, today, datesXMap)

      const dates = Object.keys(datesXMap)
      const defaultDate = today && datesXMap.hasOwnProperty(today) ? today : last(dates)
      const defaultX = datesXMap[defaultDate]
      const defaultPoint = memberItems.find((point) => point.x === defaultX)
      const defaultY = defaultPoint ? defaultPoint.y : null

      const defaultTooltips: Tooltip[] = !isNumber(defaultY) ?
        [] :
        [{
          type: 'member',
          text: formatUSCurrency(defaultY),
          x: defaultX,
          y: defaultY,
        }]

      const formatLimit = ({ title, amount }: SecondaryLine) => ({ amount, title: title.toUpperCase() })

      const avgLine = oppositeTrend && categoryId === ALL_CATEGORY_ID ? {
        amount: oppositeTrend.average,
        title: type === 'spending' ? 'YOUR AVG. INCOME' : 'YOUR AVG. SPENDING',
      } : undefined

      return {
        xWidth: dates.length,
        defaultTooltips,

        dots: [
          ...buildTrendDots(memberItems, memberProjectedItems, 'member'),
          ...buildTrendDots(peersItems, peersProjectedItems, 'peers'),
          ...buildTrendDots(otherItems, otherProjectedItems, 'others'),
        ],

        memberItems,
        memberProjectedItems,
        peersItems,
        peersProjectedItems,
        otherItems,
        otherProjectedItems,

        tips: buildTips(datesXMap, today),
        headerTips: buildTips(datesXMap, today, formatHeaderTipDate),
        dividers: buildDividersOneMonth(datesXMap),
        sections: buildSectionsOneMonth(datesXMap),
        valueDecorator: formatUSCurrency,

        limitLine: trackerLimit ? formatLimit(trackerLimit) : undefined,
        compareLine: avgLine,

        selectedPeergroupName: selectedRelation.name,
        isMobile,
        withComparison,
      }
    }
  ) as (state: State, ownProps: OwnProps) => LineChartInnerStateProps


const guards: Guards<OwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    memberMoneyflowTrendGuard, peergroupMoneyflowTrendsGuard,
    memberOppositeMoneyflowTrendsGuard, moneyflowTrackersGuard,
  ),
]

const connected = connectShadedLoadable<
  LineChartInnerStateProps,
  {},
  LineChartInnerOwnProps,
  OwnProps
>(createMapStateToProps, undefined, guards, LineChartLoadingOverlay)(LineChart)
export default connected

export const MoneyflowDailyTrendContainer = connectLoadable<
  LineChartInnerStateProps,
  {},
  LineChartInnerOwnProps,
  OwnProps
>(guards)(createMapStateToProps)(LineChart)
